import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminStore } from '../../store/adminStore';
import { adminApi } from '../../services/adminApi';
import UsersTab from './tabs/UsersTab';
import GamesTab from './tabs/GamesTab';
import MaintenanceTab from './tabs/MaintenanceTab';
import LogsTab from './tabs/LogsTab';
import AuditTab from './tabs/AuditTab';

type Tab = 'overview' | 'users' | 'games' | 'maintenance' | 'logs' | 'audit';

interface DashboardStats {
  totals: {
    users: number;
    deletedUsers: number;
    activeGames: number;
    waitingGames: number;
    pendingWithdrawals: number;
    pendingDeposits: number;
    totalChipsMicro: string;
  };
  maintenance: {
    softLock: boolean;
    hardLock: boolean;
    message: string | null;
  };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const admin = useAdminStore((s) => s.admin);
  const adminLogout = useAdminStore((s) => s.adminLogout);
  const isAuthenticated = useAdminStore((s) => s.isAuthenticated);

  const [tab, setTab] = useState<Tab>('overview');
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  async function refreshStats() {
    try {
      const res = await adminApi.get<DashboardStats>('/dashboard');
      setStats(res.data);
    } catch {
      /* interceptor handles 401 */
    }
  }

  useEffect(() => {
    refreshStats();
    const t = setInterval(refreshStats, 10000);
    return () => clearInterval(t);
  }, []);

  function handleLogout() {
    adminLogout();
    navigate('/admin', { replace: true });
  }

  const tabBtn = (key: Tab, label: string) => (
    <button
      key={key}
      onClick={() => setTab(key)}
      className={`px-3 py-2 text-sm font-medium rounded-lg transition ${
        tab === key
          ? 'bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/40'
          : 'text-gray-300 hover:bg-white/5 border border-transparent'
      }`}
    >
      {label}
    </button>
  );

  const totalChips = stats
    ? (Number(stats.totals.totalChipsMicro) / 1_000_000).toFixed(2)
    : '0.00';

  return (
    <div className="min-h-screen bg-brand-bg text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/30 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-bold text-lg">
              <span className="text-brand-cyan">T3</span> Admin
            </h1>
            {stats && (stats.maintenance.softLock || stats.maintenance.hardLock) && (
              <span className="px-2.5 py-1 text-xs font-bold uppercase rounded-md bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse">
                {stats.maintenance.hardLock ? 'HARD LOCK' : 'SOFT LOCK'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="text-gray-400">{admin?.email}</span>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-white/5 hover:bg-white/10 border border-white/10"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-6 pb-3 flex gap-2 overflow-x-auto">
          {tabBtn('overview', 'Overview')}
          {tabBtn('users', 'Users')}
          {tabBtn('games', 'Games')}
          {tabBtn('maintenance', 'Maintenance')}
          {tabBtn('logs', 'App logs')}
          {tabBtn('audit', 'Admin audit')}
        </div>
      </header>

      {/* Body */}
      <main className="max-w-6xl mx-auto px-6 py-6">
        {tab === 'overview' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Active users" value={stats?.totals.users ?? '\u2014'} />
            <StatCard label="Soft-deleted" value={stats?.totals.deletedUsers ?? '\u2014'} />
            <StatCard label="Live games" value={stats?.totals.activeGames ?? '\u2014'} />
            <StatCard label="Waiting games" value={stats?.totals.waitingGames ?? '\u2014'} />
            <StatCard label="Pending withdrawals" value={stats?.totals.pendingWithdrawals ?? '\u2014'} />
            <StatCard label="Unconfirmed deposits" value={stats?.totals.pendingDeposits ?? '\u2014'} />
            <StatCard label="Total chips in play" value={`${totalChips} mUSD`} wide />
          </div>
        )}
        {tab === 'users' && <UsersTab />}
        {tab === 'games' && <GamesTab />}
        {tab === 'maintenance' && <MaintenanceTab onChange={refreshStats} state={stats?.maintenance} />}
        {tab === 'logs' && <LogsTab />}
        {tab === 'audit' && <AuditTab />}
      </main>
    </div>
  );
}

function StatCard({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string | number;
  wide?: boolean;
}) {
  return (
    <div
      className={`bg-white/5 border border-white/10 rounded-xl p-4 ${wide ? 'col-span-2' : ''}`}
    >
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
